import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent, DialogConfig } from '@vsolv/vectors-ui/dialog';
import { Policy } from '@wsphere/warranties/domain';
import { PolicyService } from '../../services';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-edit-email-template-dialog',
  templateUrl: './edit-email-template.dialog.html',
  standalone: false,
})
export class EditEmailTemplateDialog implements OnDestroy {
  private readonly emailValidatorControl = new FormControl('', [Validators.email]);
  protected readonly scheduleAnchors = [
    { value: Policy.ScheduleAnchor.EXPIRY_DATE, label: 'Expiry Date' },
    { value: Policy.ScheduleAnchor.TERM_START_DATE, label: 'Term Start Date' },
  ];

  protected readonly emailType = Policy.EmailType;
  protected readonly systemAttachmentOptions = [
    { value: Policy.PolicyEmailSystemAttachment.TERMS_AND_CONDITIONS, label: 'Policy Terms & Conditions' },
    { value: Policy.PolicyEmailSystemAttachment.COVERAGE_SUMMARY, label: 'Policy Coverage Summary' },
    { value: Policy.PolicyEmailSystemAttachment.REGISTRATION_PAGE, label: 'Warranty Registration Page' },
  ];
  protected readonly tenantWideEmailNote =
    'Changes to this template apply across all policies and affect every login link email sent to portal users.';

  constructor(
    private formBuilder: FormBuilder,
    private policySvc: PolicyService,
    private toastSvc: ToastService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  @ViewChild(DialogComponent) dialog!: DialogComponent;
  @Input() dialogConfig?: DialogConfig;
  @Input() policy?: Policy.Model;

  saving = false;
  copiedToken: string | null = null;

  private copiedTokenTimeout: ReturnType<typeof setTimeout> | null = null;
  offsetInput = '';
  recipientInput = '';
  offsetError = '';
  recipientError = '';

  email!: Policy.Email | null;

  get tokens(): Policy.EmailToken[] {
    return (this.email ? Policy.POLICY_EMAIL_TOKENS[this.email.type] : null) ?? [];
  }

  copyToken(token: string) {
    if (this.copiedTokenTimeout) clearTimeout(this.copiedTokenTimeout);

    this.copiedToken = token;
    this.toastSvc.show({ type: 'success', title: 'Copied!', text: `${token} copied to clipboard.` });
    this.copiedTokenTimeout = setTimeout(() => (this.copiedToken = null), 2000);
  }

  ngOnDestroy() {
    if (this.copiedTokenTimeout) clearTimeout(this.copiedTokenTimeout);
  }

  form = this.formBuilder.group({
    enabled: [true, Validators.required],
    templateLabel: [''],
    subject: [''],
    byline: [''],
    content: ['', []],
    scheduleAnchor: [null as Policy.ScheduleAnchor | null],
    scheduleOffsetsDays: [[] as number[]],
    additionalRecipients: [[] as string[]],
    systemAttachments: [[] as Policy.PolicyEmailSystemAttachment[]],
    uploadedAttachments: [[] as Policy.PolicyEmailUploadedAttachment[]],
    newAttachments: [[] as File[]],
  });

  resetFormValue = {
    enabled: true,
    templateLabel: '',
    subject: '',
    byline: '',
    content: '',
    scheduleAnchor: null as Policy.ScheduleAnchor | null,
    scheduleOffsetsDays: [] as number[],
    additionalRecipients: [] as string[],
    systemAttachments: [] as Policy.PolicyEmailSystemAttachment[],
    uploadedAttachments: [] as Policy.PolicyEmailUploadedAttachment[],
    newAttachments: [] as File[],
  };

  @Input() set policyEmail(policyEmail: Policy.Email | null) {
    if (policyEmail) {
      this.email = policyEmail;

      this.resetFormValue = {
        enabled: policyEmail.enabled ?? true,
        templateLabel: policyEmail.templateLabel || '',
        subject: policyEmail.subject || '',
        byline: policyEmail.byline || '',
        content: policyEmail.content || '',
        scheduleAnchor: policyEmail.scheduleAnchor ?? null,
        scheduleOffsetsDays: this.normalizeOffsets(policyEmail.scheduleOffsetsDays ?? []),
        additionalRecipients: this.normalizeRecipients(policyEmail.additionalRecipients ?? []),
        systemAttachments: [...(policyEmail.systemAttachments ?? [])],
        uploadedAttachments: [...(policyEmail.uploadedAttachments ?? [])],
        newAttachments: [],
      };
      this.offsetInput = '';
      this.recipientInput = '';
      this.offsetError = '';
      this.recipientError = '';
      this.resetForm();
    }
  }

  get isScheduledTemplate() {
    return this.email?.deliveryMode === Policy.DeliveryMode.SCHEDULED;
  }

  get deliveryModeLabel() {
    return this.isScheduledTemplate ? 'Schedule Based' : 'Event Based';
  }

  get scheduleOffsetsDays() {
    return this.form.controls.scheduleOffsetsDays.value ?? [];
  }

  get additionalRecipients() {
    return this.form.controls.additionalRecipients.value ?? [];
  }

  get isWarrantyProvisionedTemplate() {
    return (
      this.email?.type === Policy.EmailType.warrantyProvisioned &&
      this.email.deliveryMode === Policy.DeliveryMode.EVENT_DRIVEN
    );
  }

  get systemAttachments() {
    return this.form.controls.systemAttachments.value ?? [];
  }

  get uploadedAttachments() {
    return this.form.controls.uploadedAttachments.value ?? [];
  }

  get newAttachments() {
    const value = this.form.controls.newAttachments.value;
    return Array.isArray(value) ? value : value ? [value] : [];
  }

  async resetForm() {
    try {
      this.form.reset(this.resetFormValue);
      this.form.markAsPristine();
    } catch (e) {
      console.error(e);
    }
  }

  open(policyEmail: Policy.Email) {
    // Apply before open so header/editor (*ngIf="email") exist when the overlay attaches.
    this.policyEmail = policyEmail;
    this.dialog.open();
    // Dialog.open() only flushes the overlay view; refresh projected form content now.
    this.cdr.detectChanges();
  }

  close() {
    this.dialog.close();
  }

  cancel() {
    this.form.reset(this.resetFormValue);
    this.form.markAsPristine();
    this.close();
  }

  async submit() {
    if (!this.policy || !this.email || this.saving) return;
    this.saving = true;

    if (this.isScheduledTemplate) this.addOffsetFromInput();
    this.addRecipientsFromInput();
    if (
      (this.isScheduledTemplate && this.offsetError) ||
      this.recipientError ||
      this.hasInvalidAdditionalRecipients()
    ) {
      this.saving = false;
      return;
    }

    const newlyUploadedAttachments: Policy.PolicyEmailUploadedAttachment[] = [];
    try {
      if (this.isWarrantyProvisionedTemplate) {
        for (const file of this.newAttachments) {
          newlyUploadedAttachments.push(await this.policySvc.uploadEmailAttachment(this.policy.id, file));
        }
      }

      const dto: Policy.UpdatePolicyEmailRequest = {
        enabled: this.form.value.enabled ?? undefined,
        type: this.email.type,
        templateLabel: this.form.value.templateLabel,
        subject: this.form.value.subject,
        byline: this.form.value.byline,
        content: this.form.value.content,
        scheduleAnchor: this.isScheduledTemplate ? (this.form.value.scheduleAnchor ?? null) : undefined,
        scheduleOffsetsDays: this.isScheduledTemplate ? this.normalizeOffsets(this.scheduleOffsetsDays) : undefined,
        additionalRecipients: this.normalizeRecipients(this.additionalRecipients),
        ...(this.isWarrantyProvisionedTemplate && {
          systemAttachments: this.systemAttachments,
          uploadedAttachments: [...this.uploadedAttachments, ...newlyUploadedAttachments],
        }),
      };

      const updatedEmail = await this.policySvc.updateEmail(this.email.id, this.policy.id, dto);

      const updatedEmailId =
        updatedEmail && typeof updatedEmail === 'object' && 'id' in updatedEmail ? updatedEmail.id : undefined;
      if (!updatedEmailId) throw new Error('The email template could not be updated');

      this.toastSvc.show({
        type: 'success',
        title: 'Updated email template',
        text: '<strong>' + this.email.type + '</strong> has been successfully updated.',
      });

      this.close();
      this.policySvc.refreshPolicy();
    } catch (error) {
      await this.cleanupUploads(newlyUploadedAttachments);
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: this.getErrorMessage(error),
      });
    } finally {
      this.saving = false;
    }
  }

  hasSystemAttachment(type: Policy.PolicyEmailSystemAttachment) {
    return this.systemAttachments.includes(type);
  }

  toggleSystemAttachment(type: Policy.PolicyEmailSystemAttachment) {
    const next = this.hasSystemAttachment(type)
      ? this.systemAttachments.filter(value => value !== type)
      : [...this.systemAttachments, type];
    this.form.controls.systemAttachments.setValue(next);
    this.form.controls.systemAttachments.markAsDirty();
  }

  removeUploadedAttachment(storageItemId: string) {
    this.form.controls.uploadedAttachments.setValue(
      this.uploadedAttachments.filter(attachment => attachment.storageItemId !== storageItemId)
    );
    this.form.controls.uploadedAttachments.markAsDirty();
  }

  addOffsetFromInput() {
    const value = this.offsetInput.trim();
    if (!value) return;

    if (!/^[-+]?\d+$/.test(value)) {
      this.offsetError = 'Offset must be a whole number (e.g. -7, 0, +15).';
      return;
    }

    this.offsetError = '';
    const nextOffsets = this.normalizeOffsets([...this.scheduleOffsetsDays, Number(value)]);
    this.form.controls.scheduleOffsetsDays.setValue(nextOffsets);
    this.form.controls.scheduleOffsetsDays.markAsDirty();
    this.offsetInput = '';
  }

  removeOffset(offset: number) {
    const nextOffsets = this.scheduleOffsetsDays.filter(item => item !== offset);
    this.form.controls.scheduleOffsetsDays.setValue(nextOffsets);
    this.form.controls.scheduleOffsetsDays.markAsDirty();
  }

  addRecipientsFromInput() {
    const rawValue = this.recipientInput.trim();
    this.recipientError = '';
    if (!rawValue) return;

    const entries = rawValue
      .split(',')
      .map(value => value.trim())
      .filter(Boolean);

    const invalid = entries.find(value => !this.isValidEmail(value));
    if (invalid) {
      this.recipientError = `Invalid email address: ${invalid}`;
      return;
    }

    this.recipientError = '';
    const nextRecipients = this.normalizeRecipients([...this.additionalRecipients, ...entries]);
    this.form.controls.additionalRecipients.setValue(nextRecipients);
    this.form.controls.additionalRecipients.markAsDirty();
    this.recipientInput = '';
  }

  removeRecipient(recipient: string) {
    const nextRecipients = this.additionalRecipients.filter(item => item !== recipient);
    this.form.controls.additionalRecipients.setValue(nextRecipients);
    this.form.controls.additionalRecipients.markAsDirty();
  }

  private hasInvalidAdditionalRecipients() {
    return this.additionalRecipients.some(value => !this.isValidEmail(value));
  }

  private isValidEmail(value: string) {
    this.emailValidatorControl.setValue(value);
    return !this.emailValidatorControl.hasError('email');
  }

  private normalizeOffsets(offsets: number[]) {
    const deduped = Array.from(new Set(offsets.map(value => Number(value)).filter(value => Number.isInteger(value))));
    return deduped.sort((a, b) => a - b);
  }

  private normalizeRecipients(recipients: string[]) {
    return Array.from(new Set(recipients.map(value => value.trim().toLowerCase()).filter(Boolean))).sort((a, b) =>
      a.localeCompare(b)
    );
  }

  private async cleanupUploads(attachments: Policy.PolicyEmailUploadedAttachment[]) {
    const policyId = this.policy?.id;
    if (!policyId) return;
    await Promise.all(
      attachments.map(attachment =>
        this.policySvc.deleteEmailAttachment(policyId, attachment.storageItemId).catch(() => undefined)
      )
    );
  }

  private getErrorMessage(error: unknown) {
    const response = error as { error?: { message?: string } | string; message?: string };
    if (typeof response?.error === 'string') return response.error;
    return response?.error?.message ?? response?.message ?? 'Unable to save the email template.';
  }
}
